import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import OverflowChips, { OChip } from '../OverflowChips';
import { OpenPeriodForm } from '../Bookkeeping/StatutoryBooks';
import Bookkeeping from '../Bookkeeping';
import type { AwSummary } from '../../types';
import { WORKSPACE_MENUS, resolveMenu, visibleSubs, AwMenu, AwSub, AwVisibility } from './menus';
import type { AwCtx, AwPeriod } from './ctx';
import Placeholder from './Placeholder';
import { ArApPane, DashCashFlowPane, OverviewPane, TaxDeadlinesPane } from './panes/Dashboard';
import { DocsPane } from './panes/Documents';
import { DebitCreditNotesPane, PaymentsPane, PurchaseOrdersPane, SupplierBillsPane } from './panes/Purchases';
import { ExpenseCategoriesPane, ReimbursementsPane } from './panes/Expenses';
import { BankAccountsPane, ReconciliationPane, TransactionsPane, TransfersPane } from './panes/Banking';
import { ChartPane, ClosingPane, JournalPane, LedgerPane, PeriodsPane } from './panes/Accounting';
import { PndPane, Pp30Pane, TaxReportsPane } from './panes/Tax';
import { ArApAgingPane, BalanceSheetPane, PlPane, ReportsCashFlowPane } from './panes/Reports';
import { CompanyProfilePane, LoginsPane, NumberingPane, TaxSettingsPane } from './panes/Settings';
import { btn, dmy, errBox } from './ui';

/** The measure every pane shares, wide enough for the document tables (owner 2026-10-13). */
const PANE_WIDTH = 1360;

// ── Company accounting workspace (owner 2026-10-13) ─────────────────────────────────────────────
// The menu-driven face of the accounting_books module: one client company at a time, nine menus
// (Dashboard · Sales · Purchases · Expenses · Banking · Accounting · Tax · Reports · Settings), each
// submenu mounting what the system already has for that company. Phase 1 adds no bookkeeping of its
// own — it re-mounts the statutory-books panels, the Tax module's per-client view and read-only
// mirrors of the company's own portal documents. What does not exist yet is NOT shown (owner
// 2026-10-13: no grey menus) — menus.ts keeps such an entry as 'placeholder' and visibleSubs() hides it.
// Phase 2 (batches 1–4) built every one of them: no entry is 'placeholder' any more.
//
// Switched on per INSTALLATION by the app_settings key acct_workspace (Admin → Companies → Workspace),
// surfaced as me.acctWorkspace. Never a firm name, mailbox or company id in code: the same build ships
// to every tenant and only the setting tells them apart.
//
// All state rides in the search params — ?client=&menu=&sub=[&view=classic] — because this component
// is also rendered as a FocusDashboard pane with no props and no path of its own, and because a URL
// with the client in it is how colleagues hand each other a company's books today.
//
// Nothing grey, ever: a submenu that does not apply to THIS company (Thai tax documents of a merchant
// not in Thai tax mode) is not listed and not reachable from the URL, the same way a placeholder is not
// (menus.ts visibleSubs with what /summary says) — never a muted chip or a "does not apply" card.

const chipStyle: React.CSSProperties = {
  padding: '6px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 999, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer',
};
const chipActive: React.CSSProperties = { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' };
const subChipStyle: React.CSSProperties = { ...chipStyle, padding: '5px 11px', fontSize: 12, fontWeight: 600 };
const subChipActive: React.CSSProperties = { background: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid var(--accent)' };
const pillStyle: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap' };

/** Which component a submenu mounts. The default branch is a safety net only: a submenu listed in
 *  menus.ts with status 'placeholder' is hidden by the shell and never reaches it. Batch 1 of Phase 2 (owner 2026-10-13, no grey
 *  menus) made expense categories, AR/AP aging and both cash-flow submenus real panes; batch 2 did the
 *  same for the three Banking submenus (statements → lines → reconciliation → transfers); batch 3 for
 *  the three Purchases registers (purchase orders → payables & payments → purchase debit/credit notes);
 *  batch 4 for reimbursements — the last one. Every submenu in menus.ts now has a case here, so the
 *  default branch is reachable only by a future entry staged as 'placeholder'. */
function renderPane(menu: AwMenu, sub: AwSub, ctx: AwCtx, title: string): React.ReactNode {
  switch (`${menu.key}/${sub.key}`) {
    case 'dashboard/overview': return <OverviewPane ctx={ctx} />;
    case 'dashboard/ar_ap': return <ArApPane ctx={ctx} />;
    case 'dashboard/cash_flow': return <DashCashFlowPane ctx={ctx} />;
    case 'dashboard/tax_deadlines': return <TaxDeadlinesPane ctx={ctx} />;
    case 'sales/quotations': return <DocsPane ctx={ctx} kind="quotation" title={title} />;
    case 'sales/invoices': return <DocsPane ctx={ctx} kind="invoice" title={title} />;
    case 'sales/tax_invoices': return <DocsPane ctx={ctx} kind="tax_invoice" title={title} />;
    case 'sales/receipts': return <DocsPane ctx={ctx} kind="receipt" title={title} />;
    case 'sales/credit_notes': return <DocsPane ctx={ctx} kind="note" title={title} />;
    case 'purchases/purchase_orders': return <PurchaseOrdersPane ctx={ctx} />;
    case 'purchases/supplier_bills': return <SupplierBillsPane ctx={ctx} />;
    case 'purchases/debit_credit_notes': return <DebitCreditNotesPane ctx={ctx} />;
    // Bookkeeping reads ?client= from the URL the workspace already carries; `embedded` drops its header.
    case 'expenses/expense_records': return <Bookkeeping embedded />;
    case 'expenses/expense_categories': return <ExpenseCategoriesPane ctx={ctx} />;
    case 'expenses/reimbursements': return <ReimbursementsPane ctx={ctx} />;
    case 'expenses/payments': return <PaymentsPane ctx={ctx} />;
    case 'banking/bank_accounts': return <BankAccountsPane ctx={ctx} />;
    case 'banking/transactions': return <TransactionsPane ctx={ctx} />;
    case 'banking/reconciliation': return <ReconciliationPane ctx={ctx} />;
    case 'banking/transfers': return <TransfersPane ctx={ctx} />;
    case 'accounting/chart': return <ChartPane ctx={ctx} />;
    case 'accounting/journal_entries': return <JournalPane ctx={ctx} />;
    case 'accounting/general_ledger': return <LedgerPane ctx={ctx} book="general-ledger" />;
    case 'accounting/trial_balance': return <LedgerPane ctx={ctx} book="trial-balance" />;
    case 'accounting/periods': return <PeriodsPane ctx={ctx} />;
    case 'accounting/closing': return <ClosingPane ctx={ctx} />;
    case 'tax/pp30': return <Pp30Pane ctx={ctx} />;
    case 'tax/pnd': return <PndPane ctx={ctx} />;
    case 'tax/tax_reports': return <TaxReportsPane ctx={ctx} />;
    case 'reports/pl': return <PlPane ctx={ctx} />;
    case 'reports/balance_sheet': return <BalanceSheetPane ctx={ctx} />;
    case 'reports/cash_flow': return <ReportsCashFlowPane ctx={ctx} />;
    case 'reports/ar_ap_aging': return <ArApAgingPane ctx={ctx} />;
    case 'settings/company_profile': return <CompanyProfilePane ctx={ctx} />;
    case 'settings/tax_settings': return <TaxSettingsPane ctx={ctx} />;
    case 'settings/document_numbering': return <NumberingPane ctx={ctx} />;
    case 'settings/users_permissions': return <LoginsPane ctx={ctx} />;
    default: return <Placeholder title={title} state="soon" />;
  }
}

/** The gates a submenu passes before its pane mounts, in this order: placeholder → permission →
 *  period → portal account. The permission gate comes before anything that would call an API. */
function PaneHost({ menu, sub, ctx }: { menu: AwMenu; sub: AwSub; ctx: AwCtx }) {
  const { t } = useTranslation();
  const title = t(sub.labelKey);
  if (sub.status === 'placeholder') return <>{renderPane(menu, sub, ctx, title)}</>;
  const missing = (sub.modules || []).filter(k => !ctx.me.modules.includes(k));
  if (missing.length) return <Placeholder title={title} state="perm" module={missing.join(', ')} />;
  // While the period list is still loading, 'no period' is not known yet — a loader, never the CTA flash.
  if (sub.needsPeriod && ctx.periodsLoading) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  if (sub.needsPeriod && !ctx.periodId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_open_first_period')}</div>
        <OpenPeriodForm clientId={ctx.clientId} onOpened={ctx.refreshPeriods} />
      </div>
    );
  }
  if (sub.needsMerchant) {
    if (ctx.summaryErr) return <div style={errBox}>{t('aw_summary_failed')}: {ctx.summaryErr}</div>;
    if (!ctx.summary) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
    if (!ctx.summary.hasMerchant) {
      // Portal Accounts is where a merchant row is created — a link in a new tab, the Portal's convention.
      const canPortal = ctx.me.modules.includes('portal_accounts');
      return <Placeholder title={title} state="noportal" link={canPortal ? { href: '/m/portal_accounts', label: t('aw_open_portal_accounts') } : undefined} />;
    }
  }
  return <>{renderPane(menu, sub, ctx, title)}</>;
}

export default function CompanyWorkspace({ clientId, clientName, clientNo, onBack }: {
  clientId: string; clientName: string; clientNo?: string; onBack: () => void;
}) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [params, setParams] = useSearchParams();

  // ── The company summary: has a portal account, Thai tax mode, VAT flag, live counts ──────────
  // Read before the menu is resolved because the tree depends on it: a Thai-only submenu is not listed
  // for a merchant that is not in Thai tax mode, so the URL's ?sub= is resolved against what THIS company
  // shows. Unknown (still loading) resolves as listed; the shell re-resolves on arrival.
  const [summary, setSummary] = useState<AwSummary | null>(null);
  const [summaryErr, setSummaryErr] = useState('');
  useEffect(() => {
    let alive = true;
    setSummary(null); setSummaryErr('');
    api.awSummary(clientId).then((d: AwSummary) => { if (alive) setSummary(d); }).catch((e: any) => { if (alive) setSummaryErr(String(e?.message || 'error')); });
    return () => { alive = false; };
  }, [clientId]);
  const vis: AwVisibility = { thaiTaxMode: summary ? summary.thaiTaxMode : undefined };
  const { menu, sub } = resolveMenu(params.get('menu'), params.get('sub'), vis);
  const setNav = (m: string, s: string) => setParams(p => { const n = new URLSearchParams(p); n.set('menu', m); n.set('sub', s); return n; }, { replace: true });
  const toClassic = () => setParams(p => { const n = new URLSearchParams(p); n.set('view', 'classic'); return n; }, { replace: true });

  // ── Period context: one picker for every submenu, newest first, exactly as StatutoryBooks did ──
  const [periods, setPeriods] = useState<AwPeriod[] | null>(null);
  const [periodId, setPeriodId] = useState('');
  const [reload, setReload] = useState(0);
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState('');
  const loadPeriods = useCallback(async (): Promise<AwPeriod[]> => {
    try { const d: any = await api.ledgerPeriods(clientId); const ps: AwPeriod[] = d.periods || []; setPeriods(ps); return ps; }
    catch (e: any) { setPeriods([]); setErr(e.message); return []; }
  }, [clientId]);
  useEffect(() => {
    let alive = true;
    setPeriods(null); setPeriodId(''); setErr('');
    loadPeriods().then(ps => { if (alive) setPeriodId(ps[0]?.id || ''); });
    return () => { alive = false; };
  }, [loadPeriods]);
  // After a period is opened or closed: newest selected, every pane re-reads (StatutoryBooks' refreshPeriods).
  const refreshPeriods = useCallback(() => {
    loadPeriods().then(ps => { setPeriodId(ps[0]?.id || ''); setOpening(false); setReload(n => n + 1); });
  }, [loadPeriods]);
  const bump = useCallback(() => setReload(n => n + 1), []);

  if (!me) return null;
  const period = (periods || []).find(p => p.id === periodId) || null;
  const ctx: AwCtx = {
    clientId, clientName, periods: periods || [], periodsLoading: periods === null, periodId, period, periodClosed: period?.status === 'closed',
    reload, bump, refreshPeriods, me, summary, summaryErr,
  };

  const menuChips: OChip[] = WORKSPACE_MENUS.map(m => ({
    key: m.key, label: t(m.labelKey), active: m.key === menu.key, onClick: () => setNav(m.key, visibleSubs(m, vis)[0].key),
  }));
  // Only built submenus that apply to this company are listed — no grey "not in this version" chip and
  // no muted "does not apply" chip (owner 2026-10-13).
  const subChips: OChip[] = visibleSubs(menu, vis).map(s => ({
    key: s.key, label: t(s.labelKey), active: s.key === sub.key, onClick: () => setNav(menu.key, s.key),
  }));

  // ONE width for every pane (owner 2026-10-13). Giving document tables their own wider
  // measure made the page jump as the operator moved between tabs, which reads as a broken
  // layout rather than as a considered one — the widest measure wins and everything shares it.
  const width = PANE_WIDTH;

  return (
    <div style={{ maxWidth: width, margin: '0 auto', padding: '20px 16px', width: '100%' }}>
      {/* top bar: back · company · client no · period · classic */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ ...btn, fontWeight: 600 }}>
          <i className="ti ti-arrow-left" aria-hidden /> {t('pa_books_all_clients')}
        </button>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{clientName}</div>
        {clientNo && <span style={{ ...pillStyle, color: 'var(--text-tertiary)', background: 'var(--bg-app)', border: '1px solid var(--border)' }}>{clientNo}</span>}
        <div style={{ flex: 1 }} />
        {periods === null ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : !periods.length ? (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('aw_no_period')}</span>
        ) : (
          <>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_sb_period_label')}</label>
            <select value={periodId} onChange={e => setPeriodId(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
              {periods.map(x => (
                <option key={x.id} value={x.id}>
                  {dmy(x.start_date)} – {dmy(x.end_date)} · {t('pa_sb_n_entries', { count: x.entries })}
                  {x.status === 'closed' ? ` · ${t('pa_sb_status_closed')}` : ''}
                </option>
              ))}
            </select>
            {period?.status === 'closed' && (
              <span style={{ ...pillStyle, fontSize: 11, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB', padding: '2px 9px' }}>ปิดบัญชีแล้ว</span>
            )}
            {/* Once every period is closed the client still needs the next one, or the books stop. */}
            {periods.every(x => x.status === 'closed') && (
              <button onClick={() => setOpening(o => !o)} style={{ ...btn, padding: '5px 10px', fontSize: 12 }}>
                <i className="ti ti-plus" aria-hidden /> {t('pa_sb_open_next')}
              </button>
            )}
          </>
        )}
        {/* The single-page statutory books stay one click away until every panel has a home here. */}
        <button onClick={toClassic} title={t('aw_classic')} style={{ ...btn, padding: '5px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>
          <i className="ti ti-layout-list" aria-hidden /> {t('aw_classic')}
        </button>
      </div>

      {err && <div style={{ ...errBox, marginBottom: 10 }}>{err}</div>}
      {opening && <div style={{ marginBottom: 12 }}><OpenPeriodForm clientId={clientId} onOpened={refreshPeriods} /></div>}

      {/* main menu — one row, never wraps */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, marginBottom: 8 }}>
        <OverflowChips chips={menuChips} chipStyle={chipStyle} activeStyle={chipActive} />
      </div>
      {/* submenu — one row, never wraps; only built submenus are listed */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
        <OverflowChips chips={subChips} chipStyle={subChipStyle} activeStyle={subChipActive} />
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <i className={menu.icon} style={{ color: 'var(--accent)', fontSize: 15 }} aria-hidden />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t(menu.labelKey)}</span>
        <span style={{ color: 'var(--text-tertiary)' }}>›</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t(sub.labelKey)}</span>
      </div>

      {/* keyed so a pane remounts when the submenu or the period changes — its own fetches re-run */}
      <div key={`${menu.key}/${sub.key}/${periodId}`}>
        <PaneHost menu={menu} sub={sub} ctx={ctx} />
      </div>
    </div>
  );
}
